import type { CSSProperties } from 'react';
import type {
  CreatedSampleRule,
  MarketingImportAddressWarning,
  MarketingImportMergeNotice,
  MarketingImportStockWarning,
  SampleRule,
} from '@/infrastructure/FulfillmentAdminClient';
import { kstDay } from './fulfillmentTime';

/** Tokens are fixed by the wireframe spec order-management.md §3.9.2 (BR-2) — do not re-pick. */
export const MARKETING_ROW_TINT = '#F3EEFF';
export const MARKETING_ROW_HOVER_TINT = '#EBE1FF';
const MARKETING_BADGE_PURPLE = '#7C3AED';

/** Bootstrap paints cells with --bs-table-bg, so the row tint has to override the variable too. */
export const marketingRowStyle: CSSProperties & Record<'--bs-table-bg' | '--bs-table-hover-bg', string> = {
  backgroundColor: MARKETING_ROW_TINT,
  '--bs-table-bg': MARKETING_ROW_TINT,
  '--bs-table-hover-bg': MARKETING_ROW_HOVER_TINT,
};

/**
 * MKT is an order class, not a sourcing route — the purple has to carry that on its own.
 * Render it on a plain `span.badge`: react-bootstrap's Badge adds a `bg-*` class whose
 * `!important` background would paint over these tokens.
 */
export const marketingBadgeStyle: CSSProperties = {
  backgroundColor: MARKETING_BADGE_PURPLE,
  color: '#FFFFFF',
  fontWeight: 800,
  letterSpacing: '0.3px',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Window bounds arrive either date-only or as a full instant; the operator reads a Seoul day either way.
 * A date-only bound is already a day, but an instant before 09:00 KST is still the previous UTC day.
 */
export const sampleWindowDay = (value?: string | null): string => {
  if (!value) {
    return 'forever';
  }
  return DATE_ONLY.test(value) ? value : kstDay(value);
};

/**
 * What the preview folded together, in words. The desk sees a row count on screen and an order count
 * afterwards, so the screen says which rows became which orders rather than leaving the gap unexplained.
 */
export const mergeNoticeText = (notice: MarketingImportMergeNotice): string =>
  `${notice.rows} ${notice.rows === 1 ? 'row' : 'rows'} will be created as ${notice.orders} ${notice.orders === 1 ? 'order' : 'orders'} — lines sharing a recipient and address are merged.`;

/**
 * A sku FF2 stock cannot cover, in words (SSD-7525). The allocator reserves a line only when the free stock covers all of
 * it, so the desk is told that at least one of the lines asking for it will be bought rather than shipped from stock.
 * Which one depends on the order the lines are raised in, so the text names every line that asks and none in particular.
 */
export const stockWarningText = (warning: MarketingImportStockWarning): string => {
  const name = warning.productName ? `${warning.sku} (${warning.productName})` : warning.sku;
  const product = warning.fileSkus.length > 0 ? `${name}, as ${warning.fileSkus.join(', ')}` : name;
  const lines = `${warning.rows.length === 1 ? 'row' : 'rows'} ${warning.rows.join(', ')}`;
  return `${product}: this file needs ${warning.requested}, FF2 has ${warning.available} free right now. At least one line asking for it (${lines}) will be bought instead of reserved.`;
};

/**
 * A phone or postcode the sheet may have mangled, on a batch booked outside OMS, in words: the rows it sits on, then the
 * server's own line. The parcel is already booked, so it is advice the desk weighs, never a reason to stop.
 */
export const addressWarningText = (warning: MarketingImportAddressWarning): string => {
  const rows = `${warning.rowNumbers.length === 1 ? 'Row' : 'Rows'} ${warning.rowNumbers.join(', ')}`;
  return `${rows}: ${warning.message}`;
};

/** What the desk is told when the preview could not read FF2 stock: no warning is not an all-clear. */
export const STOCK_CHECK_UNAVAILABLE_TEXT =
  'FF2 stock could not be checked for this file. Lines stock cannot cover in full will be bought instead of reserved.';

/** A window booked for later can be called off as surely as a running one; only a closed one cannot. */
export const isSampleWindowCancellable = (rule: Pick<SampleRule, 'status'>): boolean =>
  rule.status === 'ACTIVE' || rule.status === 'SCHEDULED';

/** The status as the desk reads it; the server sends it upper-case. */
export const sampleWindowStatusLabel = (status: string): string => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'SCHEDULED':
      return 'Scheduled';
    case 'ENDED':
      return 'Ended';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

/** Running is green, booked is blue, and anything already closed recedes. */
export const sampleWindowStatusVariant = (status: string): string =>
  status === 'ACTIVE' ? 'success' : status === 'SCHEDULED' ? 'info' : 'secondary';

/**
 * The server keeps windows in the order they were opened and a window carries no creation time of its
 * own, so the newest is the last one it lists.
 */
export const newestSampleWindowsFirst = (rules: SampleRule[]): SampleRule[] => [...rules].reverse();

const orders = (count: number): string => `${count} ${count === 1 ? 'order' : 'orders'}`;

/** What opening a window did, as a headline and one line per order the set could not reach. */
export interface SampleWindowOutcome {
  /** Nothing went wrong: every named order now has a set, or none was named. */
  clean: boolean;
  summary: string;
  details: string[];
}

/**
 * Reads what the server said opening a window did. An all-new-orders window touches no order that
 * exists yet, so it reports zero targets now rather than leaving the desk to wonder whether it worked.
 */
export const describeSampleWindowOutcome = (created: CreatedSampleRule): SampleWindowOutcome => {
  if (created.applied === undefined && created.skipped === undefined && created.failed === undefined) {
    return {
      clean: true,
      summary: 'Assignment window opened — currently 0 target orders. It applies to orders created during the window.',
      details: [],
    };
  }
  const applied = created.applied ?? [];
  const skipped = created.skipped ?? [];
  const failed = created.failed ?? [];
  return {
    clean: skipped.length === 0 && failed.length === 0,
    summary: `Sample set applied to ${orders(applied.length)} · ${orders(skipped.length)} already had one · ${orders(failed.length)} not eligible`,
    details: [
      ...(skipped.length > 0 ? [`Already assigned: ${skipped.map((id) => `#${id}`).join(', ')}`] : []),
      ...failed.map((failure) => `#${failure.orderId}: ${failure.reason}`),
    ],
  };
};
